/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { CloudResourceReportStatus } from "~/generated/graphql";
import { PopoverControls } from "~/components";
import { useDisclosure } from "~/hooks";
import { useRerunCloudResourceReport } from "~/views/resources_v2/ResourceReports/hooks";
import { CancelModal } from "./CancelModal";
import { DeleteModal } from "./DeleteModal";

type Props = {
  status: CloudResourceReportStatus;
  id: string;
  url?: string | null;
  name: string;
};

export function Actions({ status, id, url, name }: Props) {
  const {
    isOpen: isCancelModalOpen,
    close: closeCancelModal,
    open: openCancelModal,
  } = useDisclosure(false);

  const {
    isOpen: isDeleteModalOpen,
    close: closeDeleteModal,
    open: openDeleteModal,
  } = useDisclosure(false);

  const handleDownload = () => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleDelete = () => {
    openDeleteModal();
  };

  const handleCancel = () => {
    openCancelModal();
  };

  const [rerunCloudResourceReport] = useRerunCloudResourceReport({
    reportName: name,
  });

  const handleRegenerate = () => {
    rerunCloudResourceReport({
      id: id,
    });
  };

  const preparePopoverItems = () => {
    switch (status) {
      case CloudResourceReportStatus.Completed:
        return [
          {
            label: `Download Report`,
            onClick: handleDownload,
          },
          {
            label: `Delete Report`,
            onClick: handleDelete,
          },
        ];
      case CloudResourceReportStatus.Failed:
        return [
          {
            label: `Regenerate Report`,
            onClick: handleRegenerate,
          },
          {
            label: `Delete Report`,
            onClick: handleDelete,
          },
        ];
      case CloudResourceReportStatus.InProgress:
        return [
          {
            label: `Cancel Report Generation`,
            onClick: handleCancel,
          },
          {
            label: `Download Report`,
            onClick: handleDownload,
            disabled: true,
          },
          {
            label: `Delete Report`,
            onClick: handleDelete,
            disabled: true,
          },
        ];
    }
  };
  const items = preparePopoverItems();

  return (
    <>
      <PopoverControls disabled={false} size={"small"} items={items} />
      <CancelModal
        reportId={id}
        reportName={name}
        reportStatus={status}
        isCancelModalOpen={isCancelModalOpen}
        close={closeCancelModal}
      />
      <DeleteModal
        reportId={id}
        reportName={name}
        reportStatus={status}
        isDeleteModalOpen={isDeleteModalOpen}
        close={closeDeleteModal}
      />
    </>
  );
}
